import { useState } from 'react';
import { requestCancelContract } from '../services/clients.service';

interface CancelContractProps {
    userConfig: {
        token: string;
        partnerApiKey: string;
        businessAlias: string;
        clientId: string;
    };
    contractId?: string;
}

interface CancelContractResponse {
    contractEndingDate: string;
    errorMessage: string | null;
    success: boolean;
}

/**
 * Hook for canceling contracts
 * @param props - Component props containing userConfig
 * @returns Object with mutation state and trigger function
 */
export const useCancelContract = (props: CancelContractProps) => {
    const [isMutating, setIsMutating] = useState<boolean>(false);
    const [response, setResponse] = useState<CancelContractResponse | null>(null);

    /**
     * Triggers the contract cancellation
     * @param contractId - Contract ID to cancel
     * @returns Promise with the cancellation result
     */
    const trigger = async (contractId: string): Promise<CancelContractResponse> => {
        setIsMutating(true);
        setResponse(null);

        try {

            const config = {
                headers: {
                    Authorization: `Bearer ${props.userConfig ? props.userConfig.token : null
                        }`
                },
                partnerApiKey: props.userConfig ? props.userConfig.partnerApiKey : null,
                businessAlias: props.userConfig ? props.userConfig.businessAlias : null,
                id: props.userConfig ? props.userConfig.clientId : null,
                contractId
            };

            const result = await requestCancelContract(config);

            setResponse(result.data);
            setIsMutating(false);
            return result.data;

        } catch (err: any) {

            const errorData = {
                contractEndingDate: '',
                errorMessage: err.message || 'Failed to cancel contract',
                success: false,
            };

            setResponse(errorData);
            setIsMutating(false);

            console.error("Error canceling contract:", err);
            return errorData;
        }
    };

    return {
        isMutating,
        trigger,
        response,
    };
};
